import React from "react";
import {
    Card,
    Button,
    Spin,
    Space,
    Alert,
    Modal,
    Typography,
    theme,
    Skeleton,
} from "antd";
import {
    ThunderboltOutlined,
    CrownOutlined,
    PauseCircleOutlined,
    ArrowUpOutlined,
    CloseCircleOutlined,
    ReloadOutlined,
    CheckCircleOutlined,
} from "@ant-design/icons";
import {
    useSubscription,
    useOrganizationTrialStatus,
    useTokenBalance,
} from "../../_actions/mutations/subscription";
import styles from "../styles/Billing.module.css";

const { Text, Title } = Typography;

const SubscriptionManager = ({
    orgId,
    onSubscribe,
    onCancel,
    onPause,
    onResume,
    onUpgrade,
    onRestart,
    loadingStates = {},
    isActivating: parentIsActivating = false,
}) => {
    const { token } = theme.useToken();
    const { data: currentSubscription, isLoading } = useSubscription(orgId);
    const { data: orgData } = useOrganizationTrialStatus(orgId);
    const trialUsed = orgData?.trial;
    const { data: tokenBalance } = useTokenBalance(orgId);

    const shouldPoll =
        currentSubscription?.status === "authenticated" ||
        currentSubscription?.status === "created";
    useSubscription(orgId, shouldPoll);

    const cardStyle = {
        borderRadius: token.borderRadiusLG,
        border: `1px solid ${token.colorBorderSecondary}`,
        boxShadow: "none",
        overflow: "hidden",
    };

    if (isLoading) {
        return (
            <Card
                bordered={false}
                style={cardStyle}
                styles={{ body: { padding: "24px" } }}
            >
                <Skeleton active paragraph={{ rows: 4 }} />
            </Card>
        );
    }

    if (
        !currentSubscription ||
        (currentSubscription.status === "created" && !parentIsActivating)
    ) {
        return (
            <Card
                bordered={false}
                style={cardStyle}
                styles={{ body: { padding: 0 } }}
            >
                <div
                    style={{
                        height: 4,
                        background: token.colorBorderSecondary,
                    }}
                />
                <div style={{ padding: "48px 24px", textAlign: "center" }}>
                    <div
                        style={{
                            width: 72,
                            height: 72,
                            borderRadius: "50%",
                            background: token.colorPrimaryBg,
                            border: `2px solid ${token.colorPrimaryBorder}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 auto 20px",
                        }}
                    >
                        <CrownOutlined
                            style={{ fontSize: 32, color: token.colorPrimary }}
                        />
                    </div>
                    <Title level={3} style={{ margin: "0 0 8px" }}>
                        {trialUsed ? "Trial Ended" : "No Active Subscription"}
                    </Title>
                    <Text
                        type="secondary"
                        style={{
                            display: "block",
                            marginBottom: 28,
                            fontSize: 14,
                        }}
                    >
                        {trialUsed
                            ? "Your trial has ended. Subscribe now to continue using all features."
                            : "Choose a plan to unlock AI content generation, scheduling, and more."}
                    </Text>
                    <Button
                        type="primary"
                        size="large"
                        icon={<CrownOutlined />}
                        onClick={onSubscribe}
                        style={{
                            height: 44,
                            paddingInline: 28,
                            fontSize: 15,
                            fontWeight: 600,
                        }}
                    >
                        {trialUsed ? "Subscribe Now" : "Choose a Plan"}
                    </Button>
                </div>
            </Card>
        );
    }

    const isActive = currentSubscription.is_active;
    const status = currentSubscription.status;
    const plan = currentSubscription.payment_plan;
    const endDate = currentSubscription.current_period_end;
    const cancelInitiatedBy = currentSubscription.cancel_initiated_by;
    const isTrial = currentSubscription.is_trial;
    const mode = currentSubscription.mode;
    const trialEndAt = currentSubscription.trial_end_at;

    const calculateDaysRemaining = (targetDate) => {
        if (!targetDate) return 0;
        const diffTime = new Date(targetDate) - new Date();
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    };

    const trialDaysRemaining = isTrial ? calculateDaysRemaining(trialEndAt) : 0;
    const daysUntilExpiration = endDate ? calculateDaysRemaining(endDate) : 0;
    const showActivationLoader =
        (parentIsActivating || status === "authenticated") && !isActive;

    const isCancelledByUser = cancelInitiatedBy === "user";
    const isActiveStatus = isActive && status === "active";

    const endDateLabel = isCancelledByUser
        ? "Expires On"
        : mode === "one_time"
        ? "Valid Until"
        : "Next Billing";

    const getStatusInfo = () => {
        if (isTrial && isActive)
            return {
                color: token.colorPrimary,
                accentColor: token.colorPrimary,
                label: "Free Trial",
            };
        if (isActive && !isCancelledByUser && status === "active")
            return {
                color: token.colorSuccess,
                accentColor: token.colorSuccess,
                label: "Active",
            };
        if (isCancelledByUser && isActive)
            return {
                color: token.colorWarning,
                accentColor: token.colorWarning,
                label: "Cancelling",
            };
        if (status === "paused")
            return {
                color: token.colorWarning,
                accentColor: token.colorWarning,
                label: "Paused",
            };
        return {
            color: token.colorTextTertiary,
            accentColor: token.colorBorderSecondary,
            label: "Inactive",
        };
    };

    const statusInfo = getStatusInfo();
    const isReallyActive =
        isActive && !isCancelledByUser && (status === "active" || isTrial);

    if (showActivationLoader) {
        return (
            <Card
                bordered={false}
                style={cardStyle}
                styles={{ body: { padding: "48px 24px", textAlign: "center" } }}
            >
                <Spin size="large" />
                <div
                    style={{
                        marginTop: 16,
                        color: token.colorTextSecondary,
                        fontSize: 14,
                    }}
                >
                    Payment verified. Activating your subscription...
                </div>
            </Card>
        );
    }

    return (
        <Card
            bordered={false}
            style={cardStyle}
            styles={{ body: { padding: 0 } }}
        >
            {/* Colored top accent bar */}
            <div style={{ height: 4, background: statusInfo.accentColor }} />

            <div style={{ padding: "24px" }}>
                {/* Plan name + status indicator */}
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: 12,
                        marginBottom: 20,
                        flexWrap: "wrap",
                    }}
                >
                    <div>
                        <Text
                            type="secondary"
                            style={{
                                fontSize: 11,
                                textTransform: "uppercase",
                                letterSpacing: "1px",
                                fontWeight: 600,
                                display: "block",
                                marginBottom: 6,
                            }}
                        >
                            Current Plan
                        </Text>
                        <Title level={3} style={{ margin: 0 }}>
                            {plan?.plan_name}
                        </Title>
                    </div>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 7,
                            background: token.colorFillAlter,
                            border: `1px solid ${token.colorBorderSecondary}`,
                            borderRadius: 20,
                            padding: "5px 12px",
                            flexShrink: 0,
                        }}
                    >
                        <span
                            className={isReallyActive ? styles.activePulse : ""}
                            style={{
                                display: "inline-block",
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                background: statusInfo.color,
                                flexShrink: 0,
                            }}
                        />
                        <Text
                            style={{
                                color: statusInfo.color,
                                fontWeight: 600,
                                fontSize: 13,
                                lineHeight: 1,
                            }}
                        >
                            {statusInfo.label}
                        </Text>
                    </div>
                </div>

                {/* Metrics strip */}
                {!isTrial && (
                    <div
                        style={{
                            display: "flex",
                            background: token.colorFillAlter,
                            borderRadius: token.borderRadius,
                            border: `1px solid ${token.colorBorderSecondary}`,
                            marginBottom: 20,
                            overflow: "hidden",
                            flexWrap: "wrap",
                        }}
                    >
                        <div
                            style={{
                                flex: 1,
                                minWidth: 80,
                                padding: "14px 16px",
                                borderRight: `1px solid ${token.colorBorderSecondary}`,
                            }}
                        >
                            <Text
                                type="secondary"
                                style={{
                                    fontSize: 11,
                                    display: "block",
                                    marginBottom: 4,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                }}
                            >
                                Amount
                            </Text>
                            <Text strong style={{ fontSize: 18 }}>
                                ₹{plan?.price || 0}
                            </Text>
                        </div>
                        {endDate && (
                            <div
                                style={{
                                    flex: 1,
                                    minWidth: 100,
                                    padding: "14px 16px",
                                    borderRight:
                                        daysUntilExpiration > 0
                                            ? `1px solid ${token.colorBorderSecondary}`
                                            : undefined,
                                }}
                            >
                                <Text
                                    type="secondary"
                                    style={{
                                        fontSize: 11,
                                        display: "block",
                                        marginBottom: 4,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.5px",
                                    }}
                                >
                                    {endDateLabel}
                                </Text>
                                <Text strong style={{ fontSize: 13 }}>
                                    {new Date(endDate).toLocaleDateString(
                                        "en-IN",
                                        {
                                            day: "numeric",
                                            month: "short",
                                            year: "numeric",
                                        }
                                    )}
                                </Text>
                            </div>
                        )}
                        {daysUntilExpiration > 0 && (
                            <div
                                style={{
                                    flex: 1,
                                    minWidth: 80,
                                    padding: "14px 16px",
                                }}
                            >
                                <Text
                                    type="secondary"
                                    style={{
                                        fontSize: 11,
                                        display: "block",
                                        marginBottom: 4,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.5px",
                                    }}
                                >
                                    Days Left
                                </Text>
                                <Text
                                    strong
                                    style={{
                                        fontSize: 18,
                                        color:
                                            daysUntilExpiration <= 7
                                                ? token.colorWarning
                                                : undefined,
                                    }}
                                >
                                    {daysUntilExpiration}
                                </Text>
                            </div>
                        )}
                    </div>
                )}

                {/* Token balance */}
                {tokenBalance !== null && tokenBalance !== undefined && (
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 14,
                            background: token.colorPrimaryBg,
                            border: `1px solid ${token.colorPrimaryBorder}`,
                            borderRadius: token.borderRadius,
                            padding: "14px 20px",
                            marginBottom: 20,
                        }}
                    >
                        <ThunderboltOutlined
                            style={{
                                fontSize: 24,
                                color: token.colorPrimary,
                                flexShrink: 0,
                            }}
                        />
                        <div>
                            <Text
                                type="secondary"
                                style={{
                                    fontSize: 11,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                    display: "block",
                                    marginBottom: 2,
                                }}
                            >
                                Token Balance
                            </Text>
                            <Text
                                strong
                                style={{
                                    fontSize: 22,
                                    color: token.colorPrimary,
                                    lineHeight: 1.2,
                                }}
                            >
                                {Number(tokenBalance).toLocaleString()}
                            </Text>
                        </div>
                    </div>
                )}

                {/* Alerts */}
                {isTrial && isActive && (
                    <Alert
                        type="info"
                        message={`Free Trial — ${trialDaysRemaining} ${
                            trialDaysRemaining === 1 ? "day" : "days"
                        } remaining`}
                        description={`Your trial ends on ${
                            trialEndAt
                                ? new Date(trialEndAt).toLocaleDateString()
                                : "N/A"
                        }. After the trial, your subscription will continue automatically with payment.`}
                        style={{ marginBottom: 16 }}
                        showIcon
                    />
                )}

                {!isTrial &&
                    mode === "one_time" &&
                    isActive &&
                    daysUntilExpiration <= 7 &&
                    daysUntilExpiration >= 0 && (
                        <Alert
                            type="warning"
                            message={`Plan expires in ${daysUntilExpiration} ${
                                daysUntilExpiration === 1 ? "day" : "days"
                            }`}
                            description="Renew now to continue enjoying the services."
                            style={{ marginBottom: 16 }}
                            showIcon
                            action={
                                <Button
                                    size="small"
                                    type="primary"
                                    onClick={onSubscribe}
                                >
                                    Renew Now
                                </Button>
                            }
                        />
                    )}

                {!isTrial &&
                    isCancelledByUser &&
                    isActive &&
                    mode !== "one_time" && (
                        <Alert
                            type="warning"
                            message={`Subscription cancelled — access until ${
                                endDate
                                    ? new Date(endDate).toLocaleDateString()
                                    : "end of billing period"
                            }`}
                            description="Restart your subscription to maintain uninterrupted access."
                            style={{ marginBottom: 16 }}
                            showIcon
                        />
                    )}

                {!isTrial && status === "paused" && isActive && (
                    <Alert
                        type="info"
                        message={`Subscription pauses on ${
                            endDate
                                ? new Date(endDate).toLocaleDateString()
                                : "end of billing period"
                        }`}
                        style={{ marginBottom: 16 }}
                        showIcon
                    />
                )}

                {/* Action buttons */}
                <Space wrap>
                    {isCancelledByUser && isActive && mode !== "one_time" && (
                        <Button
                            type="primary"
                            loading={loadingStates.resume}
                            icon={<ReloadOutlined />}
                            onClick={() => onRestart(currentSubscription.id)}
                        >
                            Restart Subscription
                        </Button>
                    )}

                    {!isCancelledByUser && isActiveStatus && (
                        <>
                            {mode === "one_time" ? (
                                <Button
                                    type="primary"
                                    icon={<ReloadOutlined />}
                                    onClick={onSubscribe}
                                >
                                    Renew Plan
                                </Button>
                            ) : (
                                <>
                                    {!isTrial && (
                                        <Button
                                            type="default"
                                            loading={loadingStates.pause}
                                            icon={<PauseCircleOutlined />}
                                            onClick={() => {
                                                Modal.confirm({
                                                    title: "Pause Subscription",
                                                    content:
                                                        "Are you sure you want to pause your subscription? Your next auto-renewal will be skipped.",
                                                    okText: "Yes, Pause",
                                                    okType: "primary",
                                                    cancelText: "No",
                                                    onOk: () =>
                                                        onPause(
                                                            currentSubscription.id
                                                        ),
                                                });
                                            }}
                                        >
                                            Pause
                                        </Button>
                                    )}
                                    <Button
                                        type="primary"
                                        ghost
                                        loading={loadingStates.upgrade}
                                        icon={<ArrowUpOutlined />}
                                        onClick={() =>
                                            onUpgrade(currentSubscription.id)
                                        }
                                    >
                                        Upgrade Plan
                                    </Button>
                                    <Button
                                        danger
                                        loading={loadingStates.cancel}
                                        icon={<CloseCircleOutlined />}
                                        onClick={() => {
                                            Modal.confirm({
                                                title: "Cancel Subscription",
                                                content:
                                                    "Are you sure you want to cancel? You will lose credits at the end of your billing period.",
                                                okText: "Yes, Cancel",
                                                okType: "danger",
                                                cancelText: "No",
                                                onOk: () =>
                                                    onCancel(
                                                        currentSubscription.id
                                                    ),
                                            });
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </>
                            )}
                        </>
                    )}

                    {!isTrial && status === "paused" && isActive && (
                        <Button
                            type="primary"
                            loading={loadingStates.resume}
                            icon={<CheckCircleOutlined />}
                            onClick={() => onResume(currentSubscription.id)}
                        >
                            Resume Subscription
                        </Button>
                    )}

                    {!isTrial &&
                        !isActive &&
                        (status === "cancelled" ||
                            (mode === "one_time" &&
                                (status === "expired" || !isActive))) && (
                            <Button
                                type="primary"
                                size="large"
                                icon={<CrownOutlined />}
                                onClick={onSubscribe}
                            >
                                {mode === "one_time"
                                    ? "Renew Subscription"
                                    : "Subscribe Again"}
                            </Button>
                        )}
                </Space>
            </div>
        </Card>
    );
};

export default SubscriptionManager;
